/** @jsx isolateComponent */
import { Fragment } from 'react';

import Surface from '@zapier/product-get-help/src/components/shared/Surface';
import {
  SizeType,
  KindDisplayNameType,
} from '@zapier/product-get-help/src/types';
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Icon,
  Link,
  SkeletonBlock,
  SkeletonLoader,
  Spacer,
  TextInput,
} from '@zapier/design-system';

import Browser from '../Browser';
import { DataType, ZapType } from '../useData';

const LOADING_LINES = 9;
const MIN_LOADING_PERCENTAGE = 85;

type OpenProps = {
  title: string;
  children: string;
  data: DataType;
  error?: unknown;
  isFiltered: boolean;
  isLoading: boolean;
  kindDisplayName: KindDisplayNameType;
  onFilter: (value: string) => void;
  onSelect: (item: ZapType | null) => void;
  size: SizeType;
};

type ErrorProps = {
  error: unknown;
  kindDisplayName: KindDisplayNameType;
  onSkip: () => void;
};

type NoZapsProps = {
  kindDisplayName: KindDisplayNameType;
  onSkip: () => void;
};

type NoErrorProps = {
  children: string;
  data: DataType;
  isFiltered: boolean;
  isLoading: boolean;
  kindDisplayName: KindDisplayNameType;
  onFilter: (value: string) => void;
  onSelect: (item: ZapType | null) => void;
  size: SizeType;
};

const scrollableCss = (size: SizeType) => css`
  margin: ${size === 'small'
    ? '-5px -10px -20px -10px'
    : '-5px -40px -30px -40px'};
  padding: ${size === 'small' ? '5px 10px 20px 10px' : '5px 40px 30px 40px'};
  max-height: 450px;
  overflow-y: scroll;
`;

const rootCss = css`
  display: grid;
  grid-gap: 22px;
`;

const innerCss = css`
  align-items: center;
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 26px 1fr;
`;

const getRandomLoadingLineWidth = () => {
  const percentage =
    Math.floor(Math.random() * (100 - MIN_LOADING_PERCENTAGE + 1)) +
    MIN_LOADING_PERCENTAGE;
  return `${percentage}%`;
};

/** Renders a skeleton used by `<ZapBlock>` while data is loading. */
const Loading = () => (
  <SkeletonLoader>
    <div css={rootCss}>
      {Array.from(Array(LOADING_LINES).keys()).map((_, i) => (
        <div css={innerCss} key={i}>
          <SkeletonBlock height={26} width={26} />
          <SkeletonBlock height={20} width={getRandomLoadingLineWidth()} />
        </div>
      ))}
    </div>
  </SkeletonLoader>
);

const Error = ({ error, kindDisplayName, onSkip }: ErrorProps) => (
  <p>
    We could not load your {kindDisplayName}s due to the error "{error}
    ". Please come back later or{' '}
    <Link
      href="#"
      onClick={e => {
        e.preventDefault();
        onSkip();
      }}
    >
      continue without selecting a {kindDisplayName}
    </Link>{' '}
    and provide the URL of the {kindDisplayName} in your message.
  </p>
);

const NoZaps = ({ kindDisplayName, onSkip }: NoZapsProps) => (
  <p>
    We could not find any {kindDisplayName}s in your account. If you are a
    member of multiple teams, make sure you have selected the right team or{' '}
    <Link
      href="#"
      onClick={e => {
        e.preventDefault();
        onSkip();
      }}
    >
      continue without selecting a {kindDisplayName}
    </Link>
    .
  </p>
);

const NoError = ({
  children,
  data,
  isFiltered,
  isLoading,
  kindDisplayName,
  onFilter,
  onSelect,
  size,
}: NoErrorProps) => {
  const hasData = data.length > 0;

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onFilter(e.target.value);

  return (
    <Fragment>
      <p>{children}</p>
      <Spacer height={30} />
      <TextInput
        ariaLabel={`Search your ${kindDisplayName}s ...`}
        isDisabled={isLoading}
        isErrored={isFiltered && !hasData}
        onChange={onInputChange}
        placeholder={`Search your ${kindDisplayName}s ...`}
        renderIconBefore={({ iconSize }: { iconSize: number }) => (
          <Icon name="navSearch" size={iconSize} />
        )}
        size="medium"
      />
      <Spacer height={30} />
      {isLoading ? (
        <Loading />
      ) : (
        <div css={scrollableCss(size)}>
          <Browser
            data={data}
            isFiltered={isFiltered}
            kindDisplayName={kindDisplayName}
            onSelect={onSelect}
          />
        </div>
      )}
    </Fragment>
  );
};

/** Renders `<ZapBlock>` when it is open. */
const Open = ({
  children,
  data,
  error,
  isFiltered,
  isLoading,
  kindDisplayName,
  onSelect,
  onFilter,
  size,
  title,
}: OpenProps) => {
  const hasNoZaps = !isLoading && !error && !isFiltered && data.length === 0;

  return (
    <Surface size={size} title={title}>
      {error ? (
        <Error
          error={error}
          kindDisplayName={kindDisplayName}
          onSkip={() => onSelect(null)}
        />
      ) : hasNoZaps ? (
        <NoZaps
          kindDisplayName={kindDisplayName}
          onSkip={() => onSelect(null)}
        />
      ) : (
        <NoError
          data={data}
          isFiltered={isFiltered}
          isLoading={isLoading}
          kindDisplayName={kindDisplayName}
          onFilter={onFilter}
          onSelect={onSelect}
          size={size}
        >
          {children}
        </NoError>
      )}
    </Surface>
  );
};

export default Open;
